import _ from 'lodash';
import React from 'react';
import AppLink from '../components/common/AppLink/AppLink';
import DrawerLink from '../components/common/drawers/ModuleDrawer/DrawerLink';
import EmailLink from '../components/common/EmailLink/EmailLink';
import { archiveFilter, tagFilter } from '../components/common/filters/consts';
import { IFilter } from '../components/common/filters/interfaces';
import RolesPopover from '../components/common/popover/RolesPopover';
import { DependencyOption } from '../components/tasks/StartCondition';
import TaskDuePill from '../components/tasks/TaskDuePill';
import { ROUTE_PATHS } from '../routes/Routes';
import { TaskAvailableColumns } from '../services/react-query/tasks';
import { Preference, PREFERENCES_VALID_KEYS } from '../types/Preference';
import { Task, TaskStatus, TaskStatusFilter } from '../types/Task';
import { TaskTemplate } from '../types/TaskTemplate';
import { UserConfigurableModule } from '../types/User';
import { AvailableColumn } from '../uikit/itable/ITable';
import { addOrRemoveDaysToDate, humanizeDate } from './dateUtils';
import { EVENT_STATUS_OPTIONS } from './event';
import { mapOptionsFromPreferences } from './filterUtils';
import TaskStatusProgress from '../components/tasks/TaskStatusProgress';

export const TaskColumns: AvailableColumn<Task, TaskAvailableColumns>[] = [
  {
    key: 'name',
    label: 'Name',
    render: (task) => (
      <DrawerLink module={UserConfigurableModule.TASKS} uuid={task.uuid}>
        {task.name}
      </DrawerLink>
    ),
  },
  {
    key: 'task_template_name',
    label: 'Template',
    render: (task) => <AppLink to={`/${ROUTE_PATHS.TASK_TEMPLATES}/${task.task_template_uuid}`}>{task.task_template_name}</AppLink>,
  },
  {
    key: 'event_display_name',
    label: 'Event',
    render: (task) => (
      <DrawerLink module={UserConfigurableModule.EVENTS} uuid={task.event_uuid || ''}>
        {task.event_display_name}
      </DrawerLink>
    ),
  },
  {
    key: 'account_name',
    label: 'Account Name',
    render: (task) => (
      <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={task.account_uuid || ''}>
        {task.account_name}
      </DrawerLink>
    ),
  },
  {
    key: 'account_code',
    label: 'Account Code',
    render: (task) => (
      <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={task.account_uuid || ''}>
        {task.account_code}
      </DrawerLink>
    ),
  },
  {
    key: 'employee_display_name',
    label: 'Assigned To',
    render: (task) => (
      <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={task.employee_uuid || ''}>
        {task.employee_display_name}
      </DrawerLink>
    ),
  },
  {
    key: 'employee_email',
    label: 'Assigned To Email',
    render: (task) => <EmailLink email={task.employee_email} />,
  },
  {
    key: 'start_date',
    label: 'Start Date',
    render: (task) => humanizeDate(task.start_date),
  },
  {
    key: 'due_date',
    label: 'Due Date',
    render: (task) => humanizeDate(task.due_date),
  },
  {
    key: 'status',
    label: 'Progress',
    sortable: false,
    render: (task) => <TaskStatusProgress task={task} />,
  },
  {
    key: 'due_status',
    label: 'Due Status',
    sortable: false,
    render: (task) => <TaskDuePill task={task} />,
  },
  {
    key: 'tags',
    label: 'Tags',
    sortable: false,
    render: (task) => <RolesPopover roles={_.map(task.tags, 'preference_value')} />,
  },
];

export const TaskFilters = (preferences?: Preference[], taskTemplates?: TaskTemplate[]): IFilter[] => [
  {
    label: 'Event Type',
    key: PREFERENCES_VALID_KEYS.EVENT_TYPE,
    type: 'dropdown',
    options: mapOptionsFromPreferences(preferences || [], PREFERENCES_VALID_KEYS.EVENT_TYPE),
  },
  {
    label: 'Event Location',
    key: PREFERENCES_VALID_KEYS.EVENT_LOCATION,
    type: 'dropdown',
    options: mapOptionsFromPreferences(preferences || [], PREFERENCES_VALID_KEYS.EVENT_LOCATION),
  },
  {
    label: 'Event Status',
    key: 'event_status',
    type: 'dropdown',
    options: EVENT_STATUS_OPTIONS,
  },
  {
    label: 'Task Status',
    key: 'task_status',
    type: 'dropdown',
    options: [
      { label: 'Active', value: TaskStatusFilter.ACTIVE },
      { label: 'Ready', value: TaskStatusFilter.READY },
      { label: 'In Progress', value: TaskStatusFilter.IN_PROGRESS },
      { label: 'On Hold', value: TaskStatusFilter.ON_HOLD },
      { label: 'Overdue', value: TaskStatusFilter.OVERDUE },
      { label: 'Completed', value: TaskStatusFilter.COMPLETED },
      { label: 'Not Started', value: TaskStatusFilter.NOT_STARTED },
    ],
  },

  {
    label: 'Assigned To',
    key: 'employee',
    type: 'employee',
  },
  {
    label: 'Task Type',
    key: 'task_type',
    type: 'dropdown',
    options: mapOptionsFromPreferences(preferences || [], PREFERENCES_VALID_KEYS.TASK_TYPE),
  },
  {
    label: 'Task Template',
    key: 'task_template',
    type: 'dropdown',
    options: _.map(taskTemplates, (template: { name: string; uuid: string }) => ({
      label: template.name,
      value: template.uuid,
    })),
  },
  {
    label: 'Due Date',
    key: 'due_date_range',
    type: 'date',
  },
  {
    label: 'Account',
    key: 'account',
    type: 'account',
  },
  {
    label: 'Account Manager',
    key: 'account_manager',
    type: 'employee',
  },
  tagFilter,
  archiveFilter,
];

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.NOT_STARTED]: '#c0c0c0',
  [TaskStatus.READY]: '#42A5F5',
  [TaskStatus.IN_PROGRESS]: '#AD49E1',
  [TaskStatus.ON_HOLD]: '#FFB74D',
  [TaskStatus.COMPLETED]: '#35B23D',
};

export function taskBaseColor(status: TaskStatus) {
  return TASK_STATUS_COLORS[status];
}

type TaskProgressData = {
  completed: number;
  inProgress: number;
  total: number;
  completedPct: number;
  inProgressPct: number;
};

export function getTaskProgress(task: Task): TaskProgressData {
  let completed = 0;
  let inProgress = 0;
  let total = task.steps.length;

  if (task.status === TaskStatus.COMPLETED) {
    total = total || 1;
    completed = total;
  } else if (total > 0) {
    completed = _.filter(task.steps, (s) => !!s.completed_at).length;

    inProgress = _.filter(task.steps, (s) => !!s.started_at && !s.completed_at).length;
  } else {
    total = 1;

    if (task.status === TaskStatus.IN_PROGRESS) {
      inProgress = 1;
    }
  }

  const completedPct = (completed / total) * 100;
  const inProgressPct = ((completed + inProgress) / total) * 100;

  return {
    completed,
    inProgress,
    total,
    completedPct,
    inProgressPct,
  };
}

type StartableLike = {
  start_date?: string | null;
  start_date_offset?: number | null;
  depends_on_uuid?: string | null;
};

/**
 * Returns a human-readable description of a task's start condition.
 * Priority order:
 * 1. Dependency – Task starts only after another task is completed.
 * 2. Offset – Task starts relative to event start (even if a derived start_date exists).
 * 3. Date – Task starts on an explicitly selected calendar date.
 * 4. Manual – Task has no automatic start condition.
 */
export const renderTaskStartCondition = (task: StartableLike, dependencyOptions: DependencyOption[] = [], eventStartDate?: string): string => {
  // Dependency (highest priority)
  if (task.depends_on_uuid) {
    const dep = dependencyOptions.find((d) => d.uuid === task.depends_on_uuid);
    return dep ? `After: ${dep.name}` : 'After task completion';
  }

  // Offset (even if start_date exists)
  if (typeof task.start_date_offset === 'number') {
    if (eventStartDate) {
      return `On: ${addOrRemoveDaysToDate({
        date: eventStartDate,
        days: task.start_date_offset,
      })}`;
    }

    return `In: ${task.start_date_offset} day${Math.abs(task.start_date_offset) === 1 ? '' : 's'}`;
  }

  // Explicit date
  if (task.start_date) {
    return `On: ${humanizeDate(task.start_date)}`;
  }

  // Manual
  return 'Manual';
};
